import { IconChevronLeft } from '@tabler/icons-react'

import { AstraButton, AstraChip, AstraEmpty, AstraIcon, AstraParagraph, DATE_TEMPLATES, format, Spinner } from '~/astra'
import { PractitionerChipWithTooltip } from '~/components'
import {
    Include,
    LocationScheduleActivityLog,
    PractitionerScheduleActivityLog,
    PractitionerScheduleLocationActivityLog,
    PractitionerScheduleStatusActivityLog,
    useLocation,
    usePractitioner,
} from '~/storm'

import { DateLocationCell, DatePractitionerCell, isDatePractitionerCell, useActivityLogs } from '../../../shared'
import { LocationScheduleActivityLogEntry } from './LocationScheduleActivityLogEntry'
import { PractitionerScheduleActivityLogEntry } from './PractitionerScheduleActivityLogEntry'
import { PractitionerScheduleLocationActivityLogEntry } from './PractitionerScheduleLocationActivityLogEntry'
import { PractitionerScheduleStatusActivityLogEntry } from './PractitionerScheduleStatusActivityLogEntry'

type LogEntryProps = {
    activityLogEntry:
        | PractitionerScheduleActivityLog
        | Include<PractitionerScheduleStatusActivityLog, { statusDefinition: true }>
        | Include<PractitionerScheduleLocationActivityLog, { location: true }>
        | LocationScheduleActivityLog
    selectedCell: DatePractitionerCell | DateLocationCell
}

const ActivityLogEntry = ({ activityLogEntry, selectedCell }: LogEntryProps) => {
    const pageView = isDatePractitionerCell(selectedCell) ? 'practitioner' : 'location'
    switch (activityLogEntry.entity_type) {
        case 'practitioner_schedule_activity_log':
            return <PractitionerScheduleActivityLogEntry activityLogEntry={activityLogEntry} />
        case 'location_schedule_activity_log':
            return <LocationScheduleActivityLogEntry activityLogEntry={activityLogEntry} />
        case 'practitioner_schedule_status_activity_log':
            return <PractitionerScheduleStatusActivityLogEntry statusLogEntry={activityLogEntry} />
        case 'practitioner_schedule_location_activity_log':
            return <PractitionerScheduleLocationActivityLogEntry locationLogEntry={activityLogEntry} pageView={pageView} />
        default:
            return null
    }
}

type Props = {
    cell: DatePractitionerCell | DateLocationCell
    hideLog: () => void
    isLoading: boolean
}

export const ActivityLog = ({ cell, hideLog, isLoading }: Props) => {
    const isPractitionerCell = isDatePractitionerCell(cell)
    const practitioner = usePractitioner({ where: { id: isPractitionerCell ? cell.practitionerId : -1 } })
    const location = useLocation({ where: { id: isPractitionerCell ? -1 : cell.locationId } })

    const activityLogs = useActivityLogs(cell)

    return (
        <div className="flex flex-col items-stretch">
            <div className="flex w-full items-center justify-between gap-8 border-b py-3 pr-3">
                <div className="flex items-center space-x-2">
                    <AstraButton variant="ghost" onClick={hideLog} icon={<AstraIcon icon={IconChevronLeft} />} />
                    <AstraParagraph weight="medium" variant="body">
                        Aktivitetslogg {format(cell.date, DATE_TEMPLATES.longMonth)}
                    </AstraParagraph>
                </div>
                {isPractitionerCell ? (
                    <PractitionerChipWithTooltip shortName={practitioner?.short_name ?? ''} size="medium" />
                ) : (
                    <AstraChip variant="outlined" label={location?.room_code ?? ''} size="medium" />
                )}
            </div>

            {isLoading ? (
                <div className="flex flex-col items-center gap-4 py-8">
                    <Spinner size="md" />
                    <AstraParagraph>Laster aktivitetslogg...</AstraParagraph>
                </div>
            ) : activityLogs.length === 0 ? (
                <div className="p-4">
                    <AstraEmpty text="Ingen aktiviteter enda" size="small" />
                </div>
            ) : (
                <div className="relative my-4">
                    <div className="max-h-96 overflow-y-auto px-4 pb-2">
                        <div className="relative">
                            {/* color is hardcoded because we want border-medium-rest but have to use background for technical reasons */}
                            <div className="pointer-events-none absolute bottom-0 left-8 top-0 w-[2px] bg-[#e0e1e6]" />
                            <div className="relative z-priority flex flex-col items-stretch gap-6">
                                {activityLogs.map(activityLog => (
                                    <ActivityLogEntry key={`${activityLog.entity_type}-${activityLog.id}`} activityLogEntry={activityLog} selectedCell={cell} />
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}
